import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import Grid2 from "@mui/material/Grid2";

import { addressesAPI } from "api/Community/AddressesAPI";
import { Address } from "api/Community/CommunityInterfaces";

import AddressesEmptyPage from "./AddressesEmptyPage";
import GenericRecordSummary from "components/Records/RecordDisplay/GenericRecordSummary";
import EditableFieldsTable from "components/Records/RecordDisplay/EditableFieldsTable";
import BasePaper from "components/Base/BasePaper";
import GenericTabs, {
  GenericTabItem,
} from "components/Base/GenericTabs";

import AddressInteractionHistoryTab from "./AddressDetail/AddressInteractionHistoryTab";
import AddressPeopleTab from "./AddressDetail/AddressPeopleTab";

interface AddressesDetailProps {
  address: Address | null;
  onAddressUpdated?: (updated: Address) => void;
  onAddressesCreated?: (newAddress: Address) => void;
  onClose?: () => void;
}

const AddressesDetail: React.FC<AddressesDetailProps> = ({
  address,
  onAddressUpdated,
  onAddressesCreated,
  onClose,
}) => {
  const [data, setData] = useState<Address | null>(address);
  const [saving, setSaving] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    setData(address);
    setTabIndex(0);
  }, [address]);

  const handleSave = async (
    field: keyof Address,
    value: any
  ) => {
    if (!data?.id) return;

    try {
      setSaving(true);

      const updated = await addressesAPI.updateField(
        data.id,
        field,
        value
      );

      setData(updated);
      onAddressUpdated?.(updated);
    } catch (error) {
      console.error("Error updating address:", error);
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: "center",
        }}
      >
        <AddressesEmptyPage
          onAddressesCreated={onAddressesCreated}
        />
      </Box>
    );
  }

  const tabs: GenericTabItem[] = [
    {
      id: "details",
      label: "Details",
      content: (
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <EditableFieldsTable<Address>
              record={data}
              onSave={handleSave}
              title="Postal Address"
              fields={[
                {
                  field: "street_address",
                  label: "Street Address",
                  multiline: true,
                  editable: false,
                },
                {
                  field: "city",
                  label: "City",
                  editable: false,
                },
                {
                  field: "state",
                  label: "State / Province",
                  editable: false,
                },
                {
                  field: "country",
                  label: "Country",
                  editable: false,
                },
                {
                  field: "post_code",
                  label: "Post Code",
                  editable: false,
                },
              ]}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <EditableFieldsTable<Address>
              record={data}
              onSave={handleSave}
              title="Details"
              fields={[
                {
                  field: "latitude",
                  label: "Latitude",
                  editable: false,
                },
                {
                  field: "longitude",
                  label: "Longitude",
                  editable: false,
                },
              ]}
            />
          </Grid2>
        </Grid2>
      ),
    },
    {
      id: "people",
      label: "People",
      content: (
        <AddressPeopleTab addressId={data.id} />
      ),
    },
    {
      id: "interaction-history",
      label: "Interaction History",
      content: (
        <Box sx={{ color: "text.secondary" }}>
          <AddressInteractionHistoryTab
            addressId={data.id}
          />
        </Box>
      ),
    },
  ];

  return (
    <>
      <GenericRecordSummary
        record={data}
        titleField="street_address"
        onUpdate={(_id, field, value) =>
          handleSave(
            field as keyof Address,
            value
          )
        }
        loading={saving}
        onClose={onClose}
        editable={false}
      />

      <BasePaper>
        <GenericTabs
          tabs={tabs}
          value={tabIndex}
          onChange={setTabIndex}
          keepMounted
        />
      </BasePaper>
    </>
  );
};

export default AddressesDetail;
